import { Link as RouterLink } from 'react-router';

import { routes } from '@/assets/constants/routes-no.ts';
import { SEO } from '@/lib/seo.tsx';
import useBrainSession from '@/services/BrainSession/useBrainSession.ts';
import { Button } from '@/ui/button-2.tsx';

import Breadcrumbs from '../../components/Breadcrumbs/index.ts';
import RrsHandler from '../../components/RrsHandler/index.ts';
import Section from '../../components/Section/index.ts';
import { LANG } from '../../services/Config.js';
import Disclaimer from '../Common/Disclaimer.tsx';

import { RRS_DEBT_FREE } from './RRS.ts';

const linkClassName =
  'typography-v1-legacy-p3 font-normal text-[#18A0FB]! no-underline hover:underline';

const unorderedListClassName = 'typography-v1-legacy-p3 mb-0 list-disc pl-7 [&>li]:mb-3';

export const DebtFree = () => {
  const { appStrings } = LANG;
  const { toggleChat } = useBrainSession();

  const handleDebtCheckClick = () => {
    toggleChat({
      goal: 'debt_check',
      step: 'refin_present_terms_auto_new',
    });
  };

  return (
    <>
      <SEO
        title="Bli gjeldfri: slik betaler du ned gjelden raskere"
        description="Konkrete steg for å bli gjeldfri, med regneeksempler på hvor mye du sparer ved å refinansiere og øke innbetalingene."
        path={routes.DEBT_FREE}
      />
      <RrsHandler {...RRS_DEBT_FREE} />
      <Section>
        <div className="flex max-w-[900px]! flex-col [&>button]:self-center">
          <Breadcrumbs
            className="mb-8"
            items={[
              { text: appStrings.MENU.HOME, url: routes.HOME },
              {
                text: appStrings.MENU.MY_ECONOMY,
                url: routes.MY_ECONOMY,
              },
              { text: appStrings.MENU.DEBT_FREE },
            ]}
          />
          <h1 className="mb-6 typography-v1-legacy-h2 text-[#18A0FB] sm:mb-8">
            Gjeldfri så fort som mulig?
          </h1>
          <h2 className="mb-4 typography-v1-legacy-subtitle1Bold">
            Det å bli gjeldfri kan virke fjernt, men er langt fra umulig. Vil du bli gjeldfri så
            fort som mulig? Vi har rådene.
          </h2>
          <p className="mb-8 typography-v1-legacy-subtitle2 sm:mb-14">
            Nordmenn er et av verdens mest gjeldstyngede folk. Det er enkelt å låne penger, og mange
            velger å bruke kreditt de egentlig ikke har. Samtidig er det nok også flere som ikke
            prioriterer å betale tilbake på et lån raskere enn de trenger, gitt at de har
            muligheten. Men det å bli gjeldfri vil gi deg større økonomisk frihet – og hva er vel
            bedre enn det?
          </p>
          <Button color="yellow" onClick={handleDebtCheckClick}>
            Bli gjeldfri raskere
          </Button>
        </div>
      </Section>
      <Section background="paper" last>
        <div className="mx-auto flex w-full max-w-[900px]! flex-col sm:flex-row">
          <div className="w-full sm:w-1/2 sm:pr-6">
            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Hvordan bli gjeldfri raskest mulig?
            </h3>
            <p className="mb-4 typography-v1-legacy-p3">
              Hvis du ønsker å bli gjeldfri fort er det tre ting du bør gjøre:
            </p>
            <ul className={unorderedListClassName}>
              <li>
                <strong> Skaff deg en oversikt over økonomien din.</strong> Sjekk kontoutskriftene
                og se hva du bruker penger på. Sett opp inntekten din pluss alle faste og variable
                utgifter.
              </li>
              <li>
                <strong>Få oversikt over all usikret gjeld.</strong> Ved å logge inn på{' '}
                <RouterLink to={routes.MY_DEBT} className={linkClassName}>
                  Min gjeld
                </RouterLink>{' '}
                kan du se hva du har i usikret gjeld, altså forbrukslån, kredittkort og
                handlekontoer. Informasjonen hentes fra Gjeldsregisteret og er oppdatert i sanntid.
              </li>
              <li>
                <strong>Kutt dårlige vaner.</strong> Når du ser hva du bruker penger på, oppdager du
                kanskje flere ting du kan kutte ut. Dropp å kjøpe deg kaffe, noe digg fra kiosken
                eller lunsj flere ganger i uka. Kanskje du heller kan ta med deg både kaffe og
                matpakke hjemmefra? Gå også over abonnementene dine for å se om det er flere du ikke
                bruker. Pengene du sparer på slike småbeløp kan du heller bruke på å bli gjeldfri
                raskere.
              </li>
              <li>
                <strong>Sørg for å betale.</strong> Når du har fått oversikt og kommet over dårlige
                vaner, gjenstår det å faktisk betale. Du bør prioritere å betale ned den usikrede
                gjelden først, for den er dyrest. Hvis du vil bli helt gjeldfri etter at forbrukslån
                og kredittkortgjeld er nedbetalt, kan du deretter betale ned boliglån og studielån
                til slutt.
              </li>
            </ul>
          </div>

          <div className="w-full sm:w-1/2 sm:pl-6">
            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">Når bør man være gjeldfri?</h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Det er ingen fasit på når du bør være gjeldfri, fordi uttrykket «så raskt som mulig»
              varierer fra person til person. For mens noen kan klare å kvitte seg med all gjeld i
              løpet av bare noen måneder, tar det lengre tid for andre.
              <br />
              <br />
              Når du tar opp forbrukslån i dag kan du betale det ned så fort du vil, men innen maks
              fem år. Samtidig vil tiden det tar å nedbetale et lån avhenge av flere faktorer, som
              størrelsen på lånet og hvor mye du betaler i de månedlige avdragene. Her spiller også
              rentene en rolle i hvor stor totalen blir. Du betaler minimum det som står på
              regningen, men kan når som helst betale ned mer hvis du vil. Husk at jo lenger tid du
              bruker på å bli gjeldfri, desto mer betaler du i renter og eventuelle gebyrer. Det
              gjør at totalsummen blir høyere enn den trenger å være.
            </p>

            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Dette kan du gjøre for å bli gjeldfri
            </h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Det aller viktigste er å gjøre det beste ut av utgangspunktet ditt. I tillegg til
              tipsene over kan du se om det er enda flere unødvendige kostnader du kan fjerne for å
              betale mer enn det du gjør i dag. Kanskje er det verdt å ta en liten ekstrajobb ved
              siden av for å få inn mer penger en periode? Eller kan du selge ting du ikke bruker
              eller trenger? Alt vil hjelpe.
              <br />
              <br />
              Hvis du sitter på flere usikrede lån og lurer på hvor du skal begynne for å bli
              gjeldfri, er et smart første steg å{' '}
              <RouterLink to={routes.PRODUCTS_REFINANCING} className={linkClassName}>
                refinansiere
              </RouterLink>{' '}
              gjelden din. Ved å samle lån og dyre kredittkort til ett lån får du ikke bare bedre
              oversikt og kun én regning å betale, men også mest sannsynlig lavere rente enn du har
              i dag.{' '}
              <RouterLink to={routes.PRODUCTS_REFINANCING} className={linkClassName}>
                Søk via Scoopr
              </RouterLink>
              , så gjør vi jobben for deg – og du kan enkelt se hvor mye penger du sparer på en
              bedre avtale. Mellomlegget kan du jo bruke på å nedbetale lånet raskere?
            </p>
          </div>
        </div>

        <div className="flex flex-col items-center [&>button]:self-center">
          <Button color="yellow" onClick={handleDebtCheckClick}>
            Bli gjeldfri raskere
          </Button>
          <Disclaimer />
        </div>
      </Section>
    </>
  );
};

export default DebtFree;
